import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useAdminAuthStore } from '../../store/adminAuthStore';
import { useAdminNotifications, useMarkNotificationRead } from '../hooks/useAdminNotifications';
import { cn } from '../../utils/cn';

function breadcrumbFromPath(pathname: string) {
  const parts = pathname.replace(/^\/admin\/?/, '').split('/').filter(Boolean);
  if (parts.length === 0) return 'Dashboard';
  const label = parts[0].replace(/-/g, ' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function timeAgo(input: string) {
  const diffMs = Date.now() - new Date(input).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function AdminHeader() {
  const location = useLocation();
  const admin = useAdminAuthStore((s) => s.admin);
  const [bellOpen, setBellOpen] = useState(false);
  const { data: notifications = [], isLoading } = useAdminNotifications();
  const markRead = useMarkNotificationRead();
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const initials = admin ? `${admin.firstName[0] ?? ''}${admin.lastName[0] ?? ''}`.toUpperCase() : 'A';

  return (
    <header className="sticky top-0 z-20 flex items-center justify-between gap-4 border-b border-beige bg-ivory/95 backdrop-blur px-5 py-3 md:px-8">
      <div className="flex items-center gap-2 pl-9 md:pl-0">
        <span className="text-xs uppercase tracking-widest2 text-charcoal/40">Admin</span>
        <span className="text-charcoal/30">/</span>
        <span className="text-sm font-semibold text-forest-dark">{breadcrumbFromPath(location.pathname)}</span>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative">
          <button
            onClick={() => setBellOpen((v) => !v)}
            className="relative inline-flex items-center justify-center h-9 w-9 rounded-full border border-beige bg-white text-charcoal/70 hover:text-forest hover:border-forest/40"
            aria-label="Notifications"
          >
            <Bell size={16} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-error text-[10px] font-semibold text-ivory">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {bellOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setBellOpen(false)} />
              <div className="absolute right-0 z-20 mt-2 w-80 rounded-lg border border-beige bg-white shadow-card">
                <div className="border-b border-beige px-4 py-3">
                  <p className="text-sm font-semibold text-forest-dark">Notifications</p>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {isLoading && <p className="px-4 py-6 text-center text-xs text-charcoal/40">Loading…</p>}
                  {!isLoading && notifications.length === 0 && (
                    <p className="px-4 py-6 text-center text-xs text-charcoal/40">You're all caught up.</p>
                  )}
                  {notifications.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => !n.isRead && markRead.mutate(n.id)}
                      className={cn(
                        'w-full text-left px-4 py-3 border-b border-beige/60 last:border-0 hover:bg-cream/50 transition-colors',
                        !n.isRead && 'bg-forest/5',
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xs font-semibold text-charcoal">{n.title}</p>
                        {!n.isRead && <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-bamboo" />}
                      </div>
                      <p className="text-xs text-charcoal/60 mt-0.5">{n.message}</p>
                      <p className="text-[10px] text-charcoal/35 mt-1">{timeAgo(n.createdAt)}</p>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-forest text-xs font-semibold text-ivory">
            {initials}
          </span>
          <span className="hidden sm:inline text-sm text-charcoal/80">{admin?.firstName}</span>
        </div>
      </div>
    </header>
  );
}
